// ABOUTME: Keeps the keys of Cloud Storage addresses — an upload session's id, a signed link's signature — out of Sentry:
// ABOUTME: their query is cut off in breadcrumbs, spans and replay records (docs/DISCUSSION_ATTACHMENTS_SPEC.md §4.4).
import type * as Sentry from "@sentry/react"
import type { Breadcrumb, BrowserOptions } from "@sentry/react"

/**
 * A storage address and its query. Whoever holds an upload session's address uploads into it, and whoever holds a
 * video's or a file's signed link plays or downloads it while it works (gql-api docs/frontend-video.md §3.1, §5;
 * frontend-files.md §6) — both keys are in the query, the path names only the object.
 */
const STORAGE_ADDRESS = /(https?:\/\/(?:[a-z0-9-]+\.)*storage\.googleapis\.com\/[^\s?#"'<>]*)[?#][^\s"'<>]*/gi

export const withoutStorageKeys = (text: string): string => text.replace(STORAGE_ADDRESS, "$1")

const STORAGE_HOST = /^https?:\/\/(?:[a-z0-9-]+\.)*storage\.googleapis\.com\//i

/** A request to the storage itself — an upload's chunk: no span is made for it. */
export const isStorageRequest = (url: string): boolean => STORAGE_HOST.test(url)

type SpanJSON = Parameters<NonNullable<BrowserOptions["beforeSendSpan"]>>[0]

const mentionsStorage = (value: unknown): value is string =>
  typeof value === "string" && value !== withoutStorageKeys(value)

/** A fetch's or an XHR's breadcrumb carries the request's address whole. */
export const scrubBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb => {
  const url: unknown = breadcrumb.data?.url
  if (!mentionsStorage(url)) return breadcrumb
  return { ...breadcrumb, data: { ...breadcrumb.data, url: withoutStorageKeys(url) } }
}

/**
 * A request span cuts the query off its name only: its `url` and `http.url` keep the address whole, and `http.query`
 * is the query itself (@sentry/browser tracing/request.js:291-303, @sentry/core fetch.js:326-330). A resource span —
 * a `<video>`'s or an `<img>`'s load — is named by the address (browser-utils metrics/browserMetrics.js:631).
 */
export const scrubSpan = (span: SpanJSON): SpanJSON => {
  const { "http.query": _query, "http.fragment": _fragment, ...data } = span.data
  const isStorage = [span.description, data.url, data["http.url"]].some(mentionsStorage)
  if (!isStorage) return span
  return {
    ...span,
    description: span.description === undefined ? undefined : withoutStorageKeys(span.description),
    data: {
      ...data,
      ...(typeof data.url === "string" && { url: withoutStorageKeys(data.url) }),
      ...(typeof data["http.url"] === "string" && { "http.url": withoutStorageKeys(data["http.url"]) }),
    },
  }
}

type ReplayOptions = NonNullable<Parameters<typeof Sentry.replayIntegration>[0]>
type RecordingEvent = Parameters<NonNullable<ReplayOptions["beforeAddRecordingEvent"]>>[0]

/**
 * A replay records a resource's load under its address, past the breadcrumbs (replay createResourceEntry,
 * index.js:5336-5366); its requests come from breadcrumbs already cut by `scrubBreadcrumb` (`_prepareXhrData`).
 */
export const scrubRecordingEvent = (event: RecordingEvent): RecordingEvent => {
  if (event.data.tag !== "performanceSpan" || !mentionsStorage(event.data.payload.description)) return event
  return {
    ...event,
    data: {
      ...event.data,
      payload: { ...event.data.payload, description: withoutStorageKeys(event.data.payload.description) },
    },
  } as RecordingEvent
}
